import { useState } from "react";
import { BsX } from "react-icons/bs";
import { toast } from "react-toastify";
import DateRangePicker from "../inputFields/DateRangePicker";
import { downloadSalesSheet } from "../../services/downloadSheets";

const ORDER_TYPES = [
  { value: "B2C", label: "B2C" },
  { value: "B2B", label: "B2B" },
  { value: "THIRD_PARTY", label: "Third Party" },
];

const STATUSES = [
  { value: "PENDING", label: "Pending" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "IN_PACKAGING", label: "In Packaging" },
  { value: "READY_TO_DISPATCH", label: "Ready to Dispatch" },
  { value: "DISPATCHED", label: "Dispatched" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "RETURNED", label: "Returned" },
];
// Same as the server default: everything except Pending
const DEFAULT_STATUSES = STATUSES.map((s) => s.value).filter((v) => v !== "PENDING");

const chip = (on) =>
  `h-9 rounded-full border px-3 text-sm font-semibold transition-colors ${on ? "border-primary bg-primary text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`;

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

// Sales sheet download (sales-new-sheet-export) from the Sales Stats screen
const SalesSheetExportModal = ({ startDate, endDate, onClose }) => {
  const [range, setRange] = useState({ start: startDate, end: endDate });
  const [types, setTypes] = useState(["B2C", "B2B"]);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [split, setSplit] = useState(true);
  const [busy, setBusy] = useState(false);

  const canDownload = range.start && range.end && types.length && statuses.length && !busy;

  const download = async () => {
    setBusy(true);
    try {
      await downloadSalesSheet({
        start_date: range.start,
        end_date: range.end,
        order_type: types.join(","),
        include_status: statuses.join(","),
        split_files: types.length > 1 && split,
      });
      toast.success("Sales sheet downloaded");
      onClose();
    } catch (e) {
      // Errors come back as a blob; read the server's message out of it
      let msg = "Could not download the sales sheet";
      try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) { /* keep default */ }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4 text-left backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-labelledby="sales-sheet-title" className="w-full max-w-lg overflow-visible rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b p-4">
          <h3 id="sales-sheet-title" className="text-lg font-bold text-gray-800">Download Sales Sheet</h3>
          <button type="button" aria-label="Close" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <BsX size={24} />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <DateRangePicker label="Order date" value={range} onChange={(r) => setRange({ start: r.start, end: r.end })} />

          <fieldset>
            <legend className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">Order type</legend>
            <div className="flex flex-wrap gap-2">
              {ORDER_TYPES.map((t) => (
                <button key={t.value} type="button" aria-pressed={types.includes(t.value)} onClick={() => setTypes(toggle(types, t.value))} className={chip(types.includes(t.value))}>
                  {t.label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 flex w-full items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-600">
              Order status
              <span className="flex gap-3 normal-case tracking-normal">
                <button type="button" onClick={() => setStatuses(STATUSES.map((s) => s.value))} className="font-semibold text-primary hover:underline">All</button>
                <button type="button" onClick={() => setStatuses(DEFAULT_STATUSES)} className="font-semibold text-primary hover:underline">Default</button>
              </span>
            </legend>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s.value} type="button" aria-pressed={statuses.includes(s.value)} onClick={() => setStatuses(toggle(statuses, s.value))} className={chip(statuses.includes(s.value))}>
                  {s.label}
                </button>
              ))}
            </div>
          </fieldset>

          {types.length > 1 && (
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input type="checkbox" checked={split} onChange={(e) => setSplit(e.target.checked)} className="h-4 w-4 accent-primary" />
              One file per order type (downloads a .zip)
            </label>
          )}

          <button
            type="button"
            onClick={download}
            disabled={!canDownload}
            className="w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-white shadow-lg shadow-primary/30 transition-all hover:shadow-primary/50 active:scale-95 disabled:bg-gray-400 disabled:shadow-none"
          >
            {busy ? "Preparing sheet…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesSheetExportModal;
